import { useContext, useMemo } from 'react';

import FeatureGatesContext from '../../contexts/FeatureGatesContext';
import RolesContext from '../../contexts/RolesContext';
import PERMISSIONS from '../../permissionDefinitions';

import useIsDataPermissionsEnabled from './useIsDataPermissionsEnabled';

const useGrantablePermissions = ({
  isUserShowScreen,
}: {
  isUserShowScreen: boolean;
}) => {
  const { isManualKpiPermissionManagementEnabled } =
    useContext(FeatureGatesContext);
  const { currentPermissions } = useContext(RolesContext);
  const isDataPermissionsEnabled = useIsDataPermissionsEnabled();

  const dataPermissions = useMemo(() => {
    if (isDataPermissionsEnabled) {
      return [
        {
          permission: PERMISSIONS.VIEW_DATA.VIEW_REVENUE,
          isEnabledByDefault: true,
        },
        {
          permission: PERMISSIONS.VIEW_DATA.VIEW_ACCIDENTS,
          isEnabledByDefault: true,
        },
        {
          permission: PERMISSIONS.VIEW_DATA.VIEW_DRIVER_PAY,
          isEnabledByDefault: true,
        },
      ];
    }
    return [];
  }, [isDataPermissionsEnabled]);

  const configPermissions = useMemo(() => {
    const configPermissions: {
      permission: FleetOps.Permission;
      isEnabledByDefault: boolean;
    }[] = [];
    if (
      currentPermissions.includes(
        PERMISSIONS.USER_MANAGEMENT.GRANT_INTEGRATION_PERMISSIONS,
      )
    ) {
      configPermissions.push({
        permission: PERMISSIONS.DATA_MANAGEMENT.INTEGRATIONS_CONFIG_VIEW,
        isEnabledByDefault: false,
      });
      configPermissions.push({
        permission: PERMISSIONS.DATA_MANAGEMENT.INTEGRATIONS_CONFIG_MODIFY,
        isEnabledByDefault: false,
      });
    }

    if (
      currentPermissions.includes(
        PERMISSIONS.USER_MANAGEMENT.GRANT_BILLING_PERMISSIONS,
      ) ||
      isUserShowScreen
    ) {
      configPermissions.push({
        permission: PERMISSIONS.BILLING.VIEW_BILLING,
        isEnabledByDefault: false,
      });
      configPermissions.push({
        permission: PERMISSIONS.BILLING.EDIT_BILLING,
        isEnabledByDefault: false,
      });
    }

    if (currentPermissions.includes(PERMISSIONS.USER_MANAGEMENT.INVITE_USER)) {
      configPermissions.push({
        permission: PERMISSIONS.USER_MANAGEMENT.CREATE_WALLBOARD,
        isEnabledByDefault: false,
      });
    }

    if (isManualKpiPermissionManagementEnabled) {
      configPermissions.push({
        permission: PERMISSIONS.SCORECARDS.MANUAL_KPI_ENTRY,
        isEnabledByDefault: false,
      });
    }

    return configPermissions;
  }, [
    currentPermissions,
    isManualKpiPermissionManagementEnabled,
    isUserShowScreen,
  ]);

  const grantablePermissions = useMemo(() => {
    return [...dataPermissions, ...configPermissions];
  }, [configPermissions, dataPermissions]);

  return {
    dataPermissions,
    configPermissions,
    grantablePermissions,
  };
};

export default useGrantablePermissions;
